import { GridArea } from '../types/grid';
import { Position } from '../Utils';

/**
 * Default grid configuration for position mapping
 * Uses a 5x5 grid with predefined areas for each semantic position
 */
const DEFAULT_GRID_SIZE = 5;

/**
 * Maps semantic Position values to GridArea configurations
 * This creates a predictable layout where each Position maps to a specific grid region
 */
export function getGridAreaForPosition(position: Position): GridArea | null {
    // Normalize aliases to their canonical equivalents to avoid duplicate keys
    const normalized: Position = (() => {
        switch (position) {
            case Position.Top:
                return Position.TopCenter;
            case Position.Bottom:
                return Position.BottomCenter;
            case Position.Left:
                return Position.MiddleLeft;
            case Position.Right:
                return Position.MiddleRight;
            default:
                return position;
        }
    })();

    const mapping: Record<string, GridArea> = {
        // Top row positions
        [Position.TopLeft]: { 
            row: 1, 
            col: 1, 
            rows: 2, 
            cols: 2, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        [Position.TopCenter]: { 
            row: 1, 
            col: 2, 
            rows: 1, 
            cols: 3, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        [Position.TopRight]: { 
            row: 1, 
            col: 4, 
            rows: 2, 
            cols: 2, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        
        // Middle row positions
        [Position.MiddleLeft]: { 
            row: 2, 
            col: 1, 
            rows: 2, 
            cols: 2, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        [Position.Center]: { 
            row: 2, 
            col: 2, 
            rows: 3, 
            cols: 3, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        [Position.MiddleRight]: { 
            row: 2, 
            col: 4, 
            rows: 2, 
            cols: 2, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        
        // Bottom row positions
        [Position.BottomLeft]: { 
            row: 4, 
            col: 1, 
            rows: 2, 
            cols: 2, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        [Position.BottomCenter]: { 
            row: 5, 
            col: 2, 
            rows: 1, 
            cols: 3, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        
        [Position.BottomRight]: { 
            row: 4, 
            col: 4, 
            rows: 2, 
            cols: 2, 
            gridSize: DEFAULT_GRID_SIZE 
        },
        
        // Video editing layout presets
        [Position.LeftThird]: {
            row: 1,
            col: 1,
            rows: 5,
            cols: 2,  // 2/5 = 40% ≈ 1/3
            gridSize: DEFAULT_GRID_SIZE
        },
        [Position.RightThird]: {
            row: 1,
            col: 4,
            rows: 5,
            cols: 2,  // 2/5 = 40% ≈ 1/3
            gridSize: DEFAULT_GRID_SIZE
        },
        [Position.TopBanner]: {
            row: 1,
            col: 1,
            rows: 1,  // 1/5 = 20%
            cols: 5,
            gridSize: DEFAULT_GRID_SIZE
        },
        [Position.BottomBanner]: {
            row: 5,
            col: 1,
            rows: 1,  // 1/5 = 20%
            cols: 5,
            gridSize: DEFAULT_GRID_SIZE
        },
        [Position.FullWidth]: {
            row: 2,
            col: 1,
            rows: 2,  // Middle 2/5 = 40%
            cols: 5,
            gridSize: DEFAULT_GRID_SIZE
        },
        [Position.LeftPanel]: {
            row: 1,
            col: 1,
            rows: 5,
            cols: 3,  // 3/5 = 60%
            gridSize: DEFAULT_GRID_SIZE
        },
        [Position.RightPanel]: {
            row: 1,
            col: 3,
            rows: 5,
            cols: 3,  // 3/5 = 60%
            gridSize: DEFAULT_GRID_SIZE
        },
    };
    
    const result = mapping[normalized] || null;
    return result;
}

/**
 * Gets all available positions that can be mapped to grid areas
 */
export function getAvailablePositions(): Position[] {
    return [
        // Standard positions
        Position.TopLeft,
        Position.TopCenter,
        Position.TopRight,
        Position.MiddleLeft,
        Position.Center,
        Position.MiddleRight,
        Position.BottomLeft,
        Position.BottomCenter,
        Position.BottomRight,
        
        // Video editing layout presets
        Position.LeftThird,
        Position.RightThird,
        Position.TopBanner,
        Position.BottomBanner,
        Position.FullWidth,
        Position.LeftPanel,
        Position.RightPanel,
    ];
}

/**
 * Validates if a position can be mapped to a grid area
 */
export function isPositionMappable(position: Position): boolean {
    return getGridAreaForPosition(position) !== null;
}